/**
 * SNATCHED.EG — wishlist, quick add to bag, and the fly-to-cart shoe.
 *
 * - Wishlist lives in localStorage (no app, no login needed) and syncs across tabs.
 * - Quick add uses the Ajax Cart API and dispatches the theme's standard
 *   CartLinesUpdateEvent so the header bubble and cart drawer refresh exactly
 *   like the native product form.
 * - Products with several sizes open a size sheet; single-variant products add instantly.
 */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const CFG = window.SnatchedConfig || {};
const T = Object.assign(
  {
    wishlist: 'Wishlist',
    saved: 'Saved',
    save: 'Add to wishlist',
    savedLong: 'Saved to wishlist',
    remove: 'Remove',
    addToBag: 'Add to bag',
    added: 'Added to your bag',
    viewBag: 'View bag',
    selectSize: 'Select your size',
    sizeGuide: 'Full details',
    soldOut: 'Sold out',
    error: 'Something went wrong. Please try again.',
    emptyTitle: 'Your wishlist is empty',
    emptyText: 'Tap the heart on any product to save it here.',
    shopNow: 'Start shopping',
    items: 'items',
    close: 'Close',
    addedToWishlist: 'Saved to your wishlist',
    removedFromWishlist: 'Removed from your wishlist',
    lowStock: 'Only a few sizes left',
    mySize: 'My size',
    setSize: 'Find my size',
    sizeTitle: 'Your size',
    sizeText: 'Pick it once. We show what fits you and add to bag in one tap.',
    onlyMine: 'Only show products in my size',
    clearSize: 'Clear my size',
    sizeSaved: 'Your size is saved',
    fitYes: 'Your size {s} in stock',
    fitNo: 'Size {s} sold out',
    sizeWord: 'Size',
    inBag: 'In your bag',
  },
  CFG.t || {}
);
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const STORAGE_KEY = 'snatched:wishlist:v1';
const SIZE_KEY = 'snatched:my-size:v1';
const ONLY_FIT_KEY = 'snatched:only-fit:v1';
const SIZE_OPTION = /size|مقاس|taille|größe|talla|^eu$|^us$|^uk$/i;
const EU_SIZES = ['35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46', '47'];

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/* ------------------------------------------------------------------ icons */
const ICON = {
  heart:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path class="sn-heart-fill" d="M12 20.3s-7.6-4.6-9.2-9.4C1.6 7.3 3.7 4 7.1 4c2 0 3.6 1.1 4.9 2.9C13.3 5.1 14.9 4 16.9 4c3.4 0 5.5 3.3 4.3 6.9-1.6 4.8-9.2 9.4-9.2 9.4Z"/><path d="M12 20.3s-7.6-4.6-9.2-9.4C1.6 7.3 3.7 4 7.1 4c2 0 3.6 1.1 4.9 2.9C13.3 5.1 14.9 4 16.9 4c3.4 0 5.5 3.3 4.3 6.9-1.6 4.8-9.2 9.4-9.2 9.4Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg>',
  bag: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 8h14l-1.2 11.2a2 2 0 0 1-2 1.8H8.2a2 2 0 0 1-2-1.8L5 8Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><path d="M9 10V7a3 3 0 0 1 6 0v3" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><path d="M12 12.5v4M10 14.5h4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
  check: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  close: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
  trash: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  shoe:
    '<svg viewBox="0 0 64 40" aria-hidden="true"><path fill="#222" d="M3 29.5c0-3 1.4-5.6 3.8-7.2l9.6-6.4c1.6-1.1 2.9-2.6 3.8-4.4l2.2-4.6c.9-1.8 3-2.6 4.9-1.9l3.4 1.3c1.4.5 2.3 1.9 2.2 3.4l-.2 2.6c-.1 1.6 1 3 2.6 3.3l6.4 1.2c2 .4 3.9 1.3 5.4 2.6l4.6 4c2.1 1.8 3.3 4.4 3.3 7.2V31H3z"/><path fill="#fff" stroke="#222" stroke-width="1.6" d="M2 31h60v3.2c0 1.6-1.3 2.8-2.8 2.8H4.8C3.3 37 2 35.8 2 34.2z"/><path d="M24 12.5l4 1.6M22.4 15.6l4 1.6M20.6 18.6l4 1.6" stroke="#fff" stroke-width="1.5" stroke-linecap="round"/><path d="M30 25.5c6 .5 12-.7 18-3.2" stroke="#8c8c8c" stroke-width="2.2" stroke-linecap="round" fill="none"/><path d="M5 29.6h6" stroke="#8c8c8c" stroke-width="1.6" stroke-linecap="round"/></svg>',
  ruler:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 17 17 3l4 4L7 21z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><path d="m7 13 2 2M10 10l2 2M13 7l2 2" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
  spark: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" width="18" height="18"><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M6 18l2.5-2.5M15.5 8.5 18 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
};

/* ------------------------------------------------------------------ money */
function formatMoney(cents) {
  const format = CFG.moneyFormat || '{{amount}}';
  const value = Number(cents) || 0;
  const pattern = /\{\{\s*(\w+)\s*\}\}/;
  const key = (format.match(pattern) || [])[1] || 'amount';
  const group = (num, decimals, thousands, decimal) => {
    const [whole, frac] = (num / 100).toFixed(decimals).split('.');
    const w = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac && Number(frac) !== 0 ? `${w}${decimal}${frac}` : w;
  };
  let amount;
  switch (key) {
    case 'amount_no_decimals':
      amount = group(value, 0, ',', '.');
      break;
    case 'amount_with_comma_separator':
      amount = group(value, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      amount = group(value, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      amount = group(value, 2, "'", '.');
      break;
    default:
      amount = group(value, 2, ',', '.');
  }
  return format.replace(pattern, amount).replace(/<[^>]*>/g, '');
}

function priceHTML(price, compare) {
  const sale = Number(compare) > Number(price);
  return `${esc(formatMoney(price))}${sale ? ` <s>${esc(formatMoney(compare))}</s>` : ''}`;
}

/* ------------------------------------------------------------------ helpers */
function imageUrl(src, width = 360) {
  if (!src) return '';
  let url = String(src);
  if (url.startsWith('//')) url = `https:${url}`;
  try {
    const u = new URL(url, location.origin);
    u.searchParams.set('width', String(width));
    return u.toString();
  } catch {
    return url;
  }
}

function handleFromUrl(url) {
  try {
    const u = new URL(url, location.origin);
    const m = u.pathname.match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

function readJSONAttr(el, name) {
  const raw = el?.getAttribute(name);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ product data */
const productCache = new Map();

function fetchProduct(handle) {
  if (!handle) return Promise.reject(new Error('Missing handle'));
  if (!productCache.has(handle)) {
    const request = fetch(`${ROOT}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((res) => {
      if (!res.ok) throw new Error(`Product ${handle} unavailable`);
      return res.json();
    });
    request.catch(() => productCache.delete(handle));
    productCache.set(handle, request);
  }
  return productCache.get(handle);
}

function snapshotFromProduct(p) {
  return {
    handle: p.handle,
    url: `${ROOT}products/${p.handle}`,
    title: p.title,
    image: imageUrl(p.featured_image, 360),
    price: p.price,
    compare: p.compare_at_price || 0,
    available: Boolean(p.available),
  };
}

/* ------------------------------------------------------------------ wishlist store */
const Wishlist = {
  read() {
    try {
      const list = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(list) ? list.filter((i) => i && i.handle) : [];
    } catch {
      return [];
    }
  },
  write(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, 60)));
    } catch {
      /* storage full or blocked: keep working in memory for this page */
    }
    document.dispatchEvent(new CustomEvent('snatched:wishlist-change', { detail: { list } }));
  },
  has(handle) {
    return this.read().some((i) => i.handle === handle);
  },
  add(item) {
    const list = this.read().filter((i) => i.handle !== item.handle);
    list.unshift({ ...item, addedAt: Date.now() });
    this.write(list);
  },
  remove(handle) {
    this.write(this.read().filter((i) => i.handle !== handle));
  },
  patch(handle, data) {
    const list = this.read();
    const index = list.findIndex((i) => i.handle === handle);
    if (index < 0) return;
    list[index] = { ...list[index], ...data };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch {
      /* ignore */
    }
  },
};

/* ------------------------------------------------------------------ overlays */
function overlayHost() {
  const open = [...document.querySelectorAll('dialog[open]')].filter((d) => {
    if (d.classList.contains('is-closing')) return false;
    try {
      return d.matches(':modal');
    } catch {
      return false;
    }
  });
  return open[open.length - 1] || document.body;
}

let lockCount = 0;
function lockScroll(on) {
  lockCount = Math.max(0, lockCount + (on ? 1 : -1));
  document.documentElement.classList.toggle('sn-lock', lockCount > 0);
}

function makeDialog(className, inner, label) {
  const dialog = document.createElement('dialog');
  dialog.className = `sn-dialog ${className}`;
  dialog.setAttribute('aria-label', label);
  dialog.innerHTML = inner;
  document.body.appendChild(dialog);

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) closeDialog(dialog);
    if (event.target instanceof Element && event.target.closest('[data-sn-close]')) closeDialog(dialog);
  });
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeDialog(dialog);
  });
  return dialog;
}

function openDialog(dialog) {
  if (dialog.open) return;
  dialog.classList.remove('is-closing');
  dialog.__returnFocus = document.activeElement;
  dialog.showModal();
  lockScroll(true);
}

function closeDialog(dialog) {
  if (!dialog.open) return Promise.resolve();
  if (dialog.__closing) return dialog.__closing;
  dialog.__closing = new Promise((resolve) => {
    const finish = () => {
      dialog.classList.remove('is-closing');
      dialog.close();
      dialog.__closing = null;
      lockScroll(false);
      const back = dialog.__returnFocus;
      if (back && back.isConnected && typeof back.focus === 'function') back.focus({ preventScroll: true });
      resolve();
    };
    if (prefersReducedMotion()) return finish();
    dialog.classList.add('is-closing');
    setTimeout(finish, 200);
  });
  return dialog.__closing;
}

/* ------------------------------------------------------------------ toast */
let toastEl;
let toastTimer;
function showToast({ title, text = '', image = '', error = false, action = true }) {
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'sn-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    toastEl.innerHTML = `<img class="sn-toast__img" alt="" hidden><span class="sn-toast__icon" hidden>${ICON.check}</span><div class="sn-toast__text"><b></b><span></span></div><button type="button" class="sn-toast__action">${esc(T.viewBag)}</button>`;
    toastEl.querySelector('.sn-toast__action').addEventListener('click', () => {
      hideToast();
      openCartDrawer();
    });
  }
  const host = overlayHost();
  if (toastEl.parentElement !== host) host.appendChild(toastEl);

  const img = toastEl.querySelector('.sn-toast__img');
  const icon = toastEl.querySelector('.sn-toast__icon');
  img.hidden = !image;
  icon.hidden = Boolean(image);
  if (image) img.src = image;
  toastEl.querySelector('b').textContent = title;
  toastEl.querySelector('.sn-toast__text span').textContent = text;
  toastEl.querySelector('.sn-toast__action').hidden = !action || error;
  toastEl.classList.toggle('is-error', error);

  requestAnimationFrame(() => toastEl.classList.add('is-visible'));
  clearTimeout(toastTimer);
  toastTimer = setTimeout(hideToast, error ? 5000 : 3800);
}
function hideToast() {
  toastEl?.classList.remove('is-visible');
}

function openCartDrawer() {
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (drawer && typeof drawer.open === 'function') {
    [...document.querySelectorAll('dialog.sn-dialog[open]')].forEach((d) => closeDialog(d));
    drawer.open();
  } else {
    window.location.href = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || `${ROOT}cart`;
  }
}

/* ------------------------------------------------------------------ fly to cart (shoe) */
function visibleCartIcon() {
  return [...document.querySelectorAll('.header-actions__cart-icon')].find((el) => {
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.bottom > 0;
  });
}

function bump(el) {
  if (!el) return;
  el.classList.remove('sn-bump');
  void el.offsetWidth;
  el.classList.add('sn-bump');
  el.addEventListener('animationend', () => el.classList.remove('sn-bump'), { once: true });
}

function flyToCart(fromRect) {
  const cartIcon = visibleCartIcon();
  if (!cartIcon || !fromRect || !Element.prototype.animate) {
    bump(cartIcon);
    return Promise.resolve();
  }
  // This is direct feedback to a tap, so it always plays; reduced motion gets a calmer, shorter path.
  const calm = prefersReducedMotion();
  const W = 72;
  const H = 46;
  const to = cartIcon.getBoundingClientRect();
  const start = { x: fromRect.left + fromRect.width / 2, y: fromRect.top + fromRect.height / 2 };
  const end = { x: to.left + to.width / 2, y: to.top + to.height / 2 };
  const lift = calm ? 40 : Math.max(140, Math.abs(start.y - end.y) * 0.4);
  const control = {
    x: (start.x + end.x) / 2 + (end.x > start.x ? -60 : 60),
    y: Math.min(start.y, end.y) - lift,
  };
  const point = (t) => ({
    x: (1 - t) ** 2 * start.x + 2 * (1 - t) * t * control.x + t ** 2 * end.x,
    y: (1 - t) ** 2 * start.y + 2 * (1 - t) * t * control.y + t ** 2 * end.y,
  });
  const direction = end.x >= start.x ? 1 : -1;

  const host = overlayHost();
  const flyer = document.createElement('div');
  flyer.className = 'sn-flyer';
  flyer.setAttribute('aria-hidden', 'true');
  flyer.innerHTML = ICON.shoe;
  if (direction < 0) flyer.firstElementChild.style.transform = 'scaleX(-1)';
  host.appendChild(flyer);

  const steps = 32;
  const frames = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const p = point(t);
    // pop off the card like a kick, hang in the air, then shrink into the bag
    const pop = t < 0.18 ? 0.6 + (t / 0.18) * 0.65 : 1.25 - ((t - 0.18) / 0.82) * 0.95;
    const scale = calm ? 1 - t * 0.6 : Math.max(0.3, pop);
    const rotate = calm ? 0 : direction * (-18 + Math.sin(t * Math.PI) * 26 + t * 18);
    frames.push({
      offset: t,
      transform: `translate(${p.x - W / 2}px, ${p.y - H / 2}px) rotate(${rotate}deg) scale(${scale})`,
      opacity: t > 0.96 ? 0 : 1,
    });
  }
  const timing = { duration: calm ? 520 : 900, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'forwards' };

  const trails = calm
    ? []
    : [0.08, 0.16, 0.24].map((lag, n) => {
        const dot = document.createElement('span');
        dot.className = 'sn-flyer__trail';
        dot.setAttribute('aria-hidden', 'true');
        host.appendChild(dot);
        const tf = frames.map((f) => {
          const t = Math.max(0, f.offset - lag);
          const p = point(t);
          return {
            offset: f.offset,
            transform: `translate(${p.x - 4}px, ${p.y + H / 4}px) scale(${1 - t * 0.7})`,
            opacity: f.offset < lag || f.offset > 0.9 ? 0 : 0.45 - n * 0.12,
          };
        });
        dot.animate(tf, timing).finished.catch(() => {}).finally(() => dot.remove());
        return dot;
      });

  const animation = flyer.animate(frames, timing);
  return animation.finished
    .then(() => {
      bump(cartIcon);
      puff(cartIcon);
    })
    .catch(() => {})
    .finally(() => {
      flyer.remove();
      trails.forEach((d) => d.remove());
    });
}

function puff(target) {
  if (!target || !Element.prototype.animate) return;
  const r = target.getBoundingClientRect();
  const badge = document.createElement('span');
  badge.className = 'sn-plus';
  badge.textContent = '+1';
  badge.setAttribute('aria-hidden', 'true');
  badge.style.left = `${r.left + r.width / 2}px`;
  badge.style.top = `${r.top}px`;
  overlayHost().appendChild(badge);
  badge
    .animate(
      [
        { transform: 'translate(-50%, 0) scale(.6)', opacity: 0 },
        { transform: 'translate(-50%, -14px) scale(1)', opacity: 1, offset: 0.35 },
        { transform: 'translate(-50%, -30px) scale(1)', opacity: 0 },
      ],
      { duration: 800, easing: 'ease-out', fill: 'forwards' }
    )
    .finished.catch(() => {})
    .finally(() => badge.remove());
}

/* ------------------------------------------------------------------ cart */
async function fetchCart() {
  const items = document.querySelector('cart-items-component');
  if (items && typeof items.fetchCartData === 'function') return items.fetchCartData();
  const res = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!res.ok) throw new Error('Cart unavailable');
  return res.json();
}

async function addVariant(variantId, source) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
  const target = source && source.isConnected ? source : document;
  const deferred = CartLinesUpdateEvent.createPromise();

  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch((window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || `${ROOT}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sectionIds.join(','),
        sections_url: location.pathname,
      }),
    });
    const data = await res.json();

    if (!res.ok || data.status) {
      const message = data.description || data.message || T.error;
      fetchCart()
        .then((cart) =>
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { didError: true, items: cart.items, source: 'snatched-quick-add', itemCount: 0 },
          })
        )
        .catch((e) => deferred.reject(e));
      target.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      return { ok: false, message };
    }

    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'snatched-quick-add',
        itemCount: 1,
        sections: data.sections,
        didError: false,
      },
    });
    return { ok: true };
  } catch (error) {
    deferred.reject(error);
    target.dispatchEvent(new CartErrorEvent({ error: error?.message || T.error, code: 'SERVICE_UNAVAILABLE' }));
    return { ok: false, message: T.error };
  }
}

function setBusy(el, on) {
  if (!el) return;
  el.classList.toggle('sn-is-busy', on);
  el.toggleAttribute('aria-busy', on);
}

/* ------------------------------------------------------------------ in-bag state */
// The bag button stays light until that exact product is in the cart, then turns ink.
const inCart = new Set();

function syncBagButtons() {
  document.querySelectorAll('[data-sn-add][data-sn-handle].sn-icon-btn').forEach((btn) => {
    const on = inCart.has(btn.dataset.snHandle);
    btn.classList.toggle('is-in-cart', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
}

function setCartFromItems(items) {
  if (!Array.isArray(items)) return;
  inCart.clear();
  items.forEach((item) => item && item.handle && inCart.add(item.handle));
  syncBagButtons();
}

let cartSyncTimer;
function refreshCartState(delay = 0) {
  clearTimeout(cartSyncTimer);
  cartSyncTimer = setTimeout(() => {
    fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => cart && setCartFromItems(cart.items))
      .catch(() => {});
  }, delay);
}

/* ------------------------------------------------------------------ my size */
const normSize = (v) =>
  String(v ?? '')
    .toLowerCase()
    .replace(/(eu|us|uk|size|مقاس)/g, '')
    .replace(',', '.')
    .replace(/\s+/g, '')
    .trim();

const MySize = {
  get() {
    try {
      return localStorage.getItem(SIZE_KEY) || '';
    } catch {
      return '';
    }
  },
  set(value) {
    try {
      if (value) localStorage.setItem(SIZE_KEY, String(value));
      else localStorage.removeItem(SIZE_KEY);
    } catch {
      /* ignore */
    }
    syncSizeUI();
  },
  onlyFit() {
    try {
      return localStorage.getItem(ONLY_FIT_KEY) === '1';
    } catch {
      return false;
    }
  },
  setOnlyFit(on) {
    try {
      localStorage.setItem(ONLY_FIT_KEY, on ? '1' : '0');
    } catch {
      /* ignore */
    }
    syncSizeUI();
  },
};

function sizeIndexOf(product) {
  const options = Array.isArray(product.options) ? product.options : [];
  return options.map((o) => (typeof o === 'string' ? o : o.name || '')).findIndex((n) => SIZE_OPTION.test(n.trim()));
}

/** Variant in the shopper's saved size, for single-option (size-only) products. */
function variantInMySize(product) {
  const mine = normSize(MySize.get());
  if (!mine) return null;
  const idx = sizeIndexOf(product);
  if (idx < 0 || (product.options || []).length !== 1) return null;
  return product.variants.find((v) => v.available && normSize((v.options || [])[idx]) === mine) || null;
}

/** Card-level size map rendered by snippets/snatched-card-actions.liquid: "42:123|43:456|" */
function cardSizes(el) {
  const holder = el?.closest('[data-sn-sizes]') || el?.querySelector?.('[data-sn-sizes]');
  if (!holder || holder.dataset.snSized !== '1') return null;
  const map = new Map();
  (holder.dataset.snSizes || '').split('|').forEach((pair) => {
    const i = pair.lastIndexOf(':');
    if (i > 0) map.set(normSize(pair.slice(0, i)), { id: pair.slice(i + 1), label: pair.slice(0, i) });
  });
  return map;
}

function cardRoot(el) {
  return el.closest('.ctc__item, product-card');
}

function syncFitChips() {
  const mine = MySize.get();
  const key = normSize(mine);
  document.querySelectorAll('[data-sn-sizes]').forEach((holder) => {
    const root = cardRoot(holder);
    if (!root) return;
    let chip = root.querySelector('[data-sn-fit]');
    if (!chip) {
      const host = root.querySelector('.ctc__info, .product-card__content');
      if (!host) return;
      chip = document.createElement('span');
      chip.className = 'sn-fit';
      chip.dataset.snFit = '';
      host.appendChild(chip);
    }
    const map = cardSizes(holder);
    if (!key || !map) {
      chip.hidden = true;
      root.removeAttribute('data-sn-fits');
      return;
    }
    const yes = map.has(key);
    chip.hidden = false;
    chip.classList.toggle('is-no', !yes);
    chip.textContent = (yes ? `✓ ${T.fitYes}` : T.fitNo).replace('{s}', mine);
    root.setAttribute('data-sn-fits', yes ? 'yes' : 'no');
  });
}

function syncSizeUI() {
  const mine = MySize.get();
  document.querySelectorAll('[data-sn-size-label]').forEach((el) => {
    if (!el.dataset.snDefault) el.dataset.snDefault = el.textContent.trim() || T.setSize;
    el.textContent = mine ? `${T.mySize}: ${mine}` : el.dataset.snDefault;
  });
  document.querySelectorAll('[data-sn-size-open]').forEach((el) => el.classList.toggle('has-size', Boolean(mine)));
  document.documentElement.classList.toggle('sn-only-fit', Boolean(mine) && MySize.onlyFit());
  syncFitChips();
  if (sizePicker?.open) renderSizePicker();
}

let sizePicker;
function renderSizePicker() {
  const mine = normSize(MySize.get());
  const grid = sizePicker.querySelector('.sn-sheet__sizes');
  grid.innerHTML = EU_SIZES.map(
    (s) => `<button type="button" class="sn-chip${normSize(s) === mine ? ' is-active' : ''}" data-size="${s}">${s}</button>`
  ).join('');
  sizePicker.querySelector('[data-sn-only]').checked = MySize.onlyFit();
  sizePicker.querySelector('[data-sn-clear]').hidden = !mine;
}

function openSizePicker() {
  if (!sizePicker) {
    sizePicker = makeDialog(
      'sn-sheet sn-sizer',
      `<div class="sn-sheet__panel" role="document">
        <div class="sn-sheet__grab" aria-hidden="true"></div>
        <div class="sn-sizer__head">
          <span class="sn-sizer__icon">${ICON.ruler}</span>
          <div><p class="sn-sheet__title">${esc(T.sizeTitle)} <small>EU</small></p><p class="sn-sizer__text">${esc(T.sizeText)}</p></div>
          <button type="button" class="sn-close" data-sn-close aria-label="${esc(T.close)}">${ICON.close}</button>
        </div>
        <div class="sn-sheet__sizes" role="group" aria-label="${esc(T.sizeTitle)}"></div>
        <label class="sn-switch"><input type="checkbox" data-sn-only><span class="sn-switch__track" aria-hidden="true"></span><span>${esc(T.onlyMine)}</span></label>
        <div class="sn-sheet__foot"><button type="button" class="sn-linkbtn" data-sn-clear>${esc(T.clearSize)}</button></div>
      </div>`,
      T.sizeTitle
    );
    sizePicker.querySelector('.sn-sheet__sizes').addEventListener('click', (event) => {
      const chip = event.target instanceof Element ? event.target.closest('[data-size]') : null;
      if (!chip) return;
      MySize.set(chip.dataset.size);
      showToast({ title: T.sizeSaved, text: `${T.sizeWord} ${chip.dataset.size}`, action: false });
      closeDialog(sizePicker);
    });
    sizePicker.querySelector('[data-sn-only]').addEventListener('change', (event) => {
      MySize.setOnlyFit(event.target.checked);
    });
    sizePicker.querySelector('[data-sn-clear]').addEventListener('click', () => {
      MySize.set('');
      MySize.setOnlyFit(false);
    });
  }
  renderSizePicker();
  openDialog(sizePicker);
  (sizePicker.querySelector('.sn-chip.is-active') || sizePicker.querySelector('.sn-chip'))?.focus({ preventScroll: true });
}

/* Product page: pre-select the saved size, and learn the size the shopper picks. */
function mountProductPageSize() {
  if (!CFG.product) return;
  const inputs = () =>
    [...document.querySelectorAll('main variant-picker input[type="radio"][data-option-name]')].filter((i) =>
      SIZE_OPTION.test((i.dataset.optionName || '').trim())
    );
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.closest('main variant-picker')) return;
    if (!SIZE_OPTION.test((input.dataset.optionName || '').trim())) return;
    if (input.dataset.optionAvailable === 'true' && input.value) MySize.set(input.value);
  });
  const mine = normSize(MySize.get());
  if (!mine || new URLSearchParams(location.search).has('variant')) return;
  const match = inputs().find((i) => normSize(i.value) === mine);
  if (match && !match.checked && match.dataset.optionAvailable === 'true') {
    match.closest('label')?.classList.add('sn-is-mine');
    requestAnimationFrame(() => match.click());
  }
}

/* ------------------------------------------------------------------ add + celebrate */
function flashDone(el) {
  if (!el || !el.classList.contains('sn-icon-btn')) return;
  const original = el.innerHTML;
  el.classList.add('sn-is-done');
  el.innerHTML = ICON.check;
  setTimeout(() => {
    el.classList.remove('sn-is-done');
    el.innerHTML = original;
  }, 1300);
}

async function addAndCelebrate(variant, product, originEl, originRect) {
  const rect = originRect || originEl?.getBoundingClientRect();
  const flight = flyToCart(rect); // optimistic: the shoe leaves while the request runs
  const result = await addVariant(variant.id, originEl);
  await flight;
  if (result.ok) {
    if (product.handle) inCart.add(product.handle);
    syncBagButtons();
    flashDone(originEl);
    const size = variant.title && variant.title !== 'Default Title' ? `${T.sizeWord} ${variant.title}` : '';
    showToast({
      title: T.added,
      text: [product.title, size].filter(Boolean).join(' · '),
      image: imageUrl(variant.featured_image?.src || product.featured_image, 120),
    });
  } else {
    showToast({ title: result.message || T.error, error: true, action: false });
  }
  return result.ok;
}

/* ------------------------------------------------------------------ size sheet */
let sheet;
function getSheet() {
  if (sheet) return sheet;
  sheet = makeDialog(
    'sn-sheet',
    `<div class="sn-sheet__panel" role="document">
      <div class="sn-sheet__grab" aria-hidden="true"></div>
      <div class="sn-sheet__head">
        <img class="sn-sheet__img" alt="" width="76" height="76">
        <div><p class="sn-sheet__title" id="sn-sheet-title"></p><p class="sn-sheet__price"></p></div>
        <button type="button" class="sn-close" data-sn-close aria-label="${esc(T.close)}">${ICON.close}</button>
      </div>
      <div class="sn-sheet__label"><span>${esc(T.selectSize)}</span><a class="sn-sheet__link" href="#">${esc(T.sizeGuide)}</a></div>
      <div class="sn-sheet__sizes" role="group" aria-labelledby="sn-sheet-title"></div>
      <p class="sn-sheet__note" hidden></p>
    </div>`,
    T.selectSize
  );
  return sheet;
}

function optionLabel(product, variant) {
  const options = Array.isArray(product.options) ? product.options : [];
  const sizeIndex = sizeIndexOf(product);
  if (options.length > 1 && sizeIndex > -1) {
    const others = (variant.options || []).filter((_, i) => i !== sizeIndex);
    return `${variant.options[sizeIndex]}${others.length ? ` · ${others.join(' / ')}` : ''}`;
  }
  return variant.public_title || variant.title;
}

function openSizeSheet(product, originEl) {
  const dlg = getSheet();
  const sale = Number(product.compare_at_price) > Number(product.price);
  const mine = normSize(MySize.get());
  const sizeIdx = sizeIndexOf(product);
  dlg.querySelector('.sn-sheet__img').src = imageUrl(product.featured_image, 200);
  dlg.querySelector('.sn-sheet__title').textContent = product.title;
  const price = dlg.querySelector('.sn-sheet__price');
  price.classList.toggle('is-sale', sale);
  price.innerHTML = priceHTML(product.price, product.compare_at_price);
  dlg.querySelector('.sn-sheet__link').href = `${ROOT}products/${product.handle}`;

  const sizes = dlg.querySelector('.sn-sheet__sizes');
  sizes.innerHTML = product.variants
    .map((v) => {
      const isMine = mine && sizeIdx > -1 && normSize((v.options || [])[sizeIdx]) === mine;
      return `<button type="button" class="sn-chip${isMine ? ' is-mine' : ''}" data-variant="${v.id}" ${
        v.available ? '' : 'disabled aria-disabled="true"'
      } aria-label="${esc(optionLabel(product, v))}${v.available ? '' : ` — ${esc(T.soldOut)}`}">${esc(optionLabel(product, v))}</button>`;
    })
    .join('');

  const availableCount = product.variants.filter((v) => v.available).length;
  const note = dlg.querySelector('.sn-sheet__note');
  const few = availableCount > 0 && availableCount <= 2 && product.variants.length > 3;
  note.hidden = !few;
  note.innerHTML = few ? `${ICON.spark}<span>${esc(T.lowStock)}</span>` : '';

  sizes.onclick = async (event) => {
    const chip = event.target instanceof Element ? event.target.closest('.sn-chip') : null;
    if (!chip || chip.disabled) return;
    const variant = product.variants.find((v) => String(v.id) === chip.dataset.variant);
    if (!variant) return;
    sizes.querySelectorAll('.sn-chip').forEach((c) => c.classList.toggle('is-active', c === chip));
    if (sizeIdx > -1 && (product.options || []).length === 1) MySize.set(variant.options[sizeIdx]);
    const rect = chip.getBoundingClientRect();
    await closeDialog(dlg); // the shoe must fly in the page, not inside the closing sheet
    const origin = originEl && originEl.isConnected && originEl.getClientRects().length ? originEl : null;
    await addAndCelebrate(variant, product, origin, origin ? null : rect);
  };

  openDialog(dlg);
  const first = sizes.querySelector('.sn-chip.is-mine:not([disabled])') || sizes.querySelector('.sn-chip:not([disabled])');
  (first || dlg.querySelector('.sn-close'))?.focus({ preventScroll: true });
}

async function quickAdd(handle, originEl) {
  if (!handle) return;

  // One tap: the card already knows which variant is the shopper's saved size.
  const map = cardSizes(originEl);
  const mine = normSize(MySize.get());
  if (map && mine && map.has(mine)) {
    const hit = map.get(mine);
    const snap = readJSONAttr(originEl.closest('[data-sn-snapshot]'), 'data-sn-snapshot') || {};
    await addAndCelebrate(
      { id: hit.id, title: hit.label },
      { handle, title: snap.title || '', featured_image: snap.image || '' },
      originEl
    );
    return;
  }

  setBusy(originEl, true);
  let product;
  try {
    product = await fetchProduct(handle);
  } catch {
    setBusy(originEl, false);
    showToast({ title: T.error, error: true, action: false });
    return;
  }
  setBusy(originEl, false);

  const available = product.variants.filter((v) => v.available);
  if (!available.length) {
    showToast({ title: T.soldOut, text: product.title, error: true, action: false });
    return;
  }
  if (product.variants.length === 1) {
    await addAndCelebrate(available[0], product, originEl);
    return;
  }
  const mineVariant = variantInMySize(product);
  if (mineVariant) {
    await addAndCelebrate(mineVariant, product, originEl);
    return;
  }
  openSizeSheet(product, originEl);
}

/* ------------------------------------------------------------------ wishlist UI */
let drawer;
function getDrawer() {
  if (drawer) return drawer;
  drawer = makeDialog(
    'sn-drawer',
    `<div class="sn-drawer__panel" role="document">
      <div class="sn-drawer__head">
        <h2 class="sn-drawer__title">${esc(T.wishlist)} <small data-sn-wish-total></small></h2>
        <button type="button" class="sn-close" data-sn-close aria-label="${esc(T.close)}">${ICON.close}</button>
      </div>
      <div class="sn-drawer__body" data-sn-wish-body></div>
    </div>`,
    T.wishlist
  );
  return drawer;
}

function renderDrawer() {
  if (!drawer) return;
  const list = Wishlist.read();
  const body = drawer.querySelector('[data-sn-wish-body]');
  drawer.querySelector('[data-sn-wish-total]').textContent = list.length ? `(${list.length})` : '';

  if (!list.length) {
    body.innerHTML = `<div class="sn-empty">
      <span class="sn-empty__icon">${ICON.heart}</span>
      <strong>${esc(T.emptyTitle)}</strong>
      <p>${esc(T.emptyText)}</p>
      <a class="sn-btn sn-btn--primary" href="${esc(CFG.shopAllUrl || `${ROOT}collections/all`)}">${esc(T.shopNow)}</a>
    </div>`;
    return;
  }

  body.innerHTML = `<ul class="sn-wl">${list
    .map((item) => {
      const sale = Number(item.compare) > Number(item.price);
      const soldOut = item.available === false;
      return `<li class="sn-wl__item">
        <a class="sn-wl__media" href="${esc(item.url)}">${item.image ? `<img src="${esc(item.image)}" alt="" loading="lazy" width="92" height="92">` : ''}</a>
        <div class="sn-wl__info">
          <a class="sn-wl__title" href="${esc(item.url)}">${esc(item.title || item.handle)}</a>
          ${item.price != null ? `<span class="sn-wl__price${sale ? ' is-sale' : ''}">${priceHTML(item.price, item.compare)}</span>` : ''}
          <div class="sn-wl__actions">
            <button type="button" class="sn-btn sn-btn--primary sn-btn--sm" data-sn-add data-sn-handle="${esc(item.handle)}" ${soldOut ? 'disabled' : ''}>${esc(soldOut ? T.soldOut : T.addToBag)}</button>
            <button type="button" class="sn-wl__remove" data-sn-remove="${esc(item.handle)}" aria-label="${esc(T.remove)} ${esc(item.title || '')}">${ICON.trash}</button>
          </div>
        </div>
      </li>`;
    })
    .join('')}</ul>`;
}

async function refreshWishlistData() {
  const list = Wishlist.read().slice(0, 40);
  await Promise.allSettled(
    list.map((item) =>
      fetchProduct(item.handle)
        .then((p) => Wishlist.patch(item.handle, snapshotFromProduct(p)))
        .catch(() => {})
    )
  );
  renderDrawer();
}

function openWishlist() {
  const dlg = getDrawer();
  renderDrawer();
  openDialog(dlg);
  dlg.querySelector('.sn-close')?.focus({ preventScroll: true });
  refreshWishlistData();
}

function syncWishlistUI() {
  const list = Wishlist.read();
  const handles = new Set(list.map((i) => i.handle));

  document.querySelectorAll('[data-sn-wish-count]').forEach((el) => {
    el.textContent = list.length > 99 ? '99+' : String(list.length);
    el.hidden = list.length === 0;
  });
  document.querySelectorAll('[data-sn-wish-open]').forEach((el) => {
    el.setAttribute('aria-label', `${T.wishlist}${list.length ? ` (${list.length} ${T.items})` : ''}`);
  });
  document.querySelectorAll('[data-sn-wish][data-sn-handle]').forEach((btn) => {
    const on = handles.has(btn.dataset.snHandle);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    if (btn.classList.contains('sn-pdp-wish')) {
      const label = btn.querySelector('[data-sn-label]');
      if (label) label.textContent = on ? T.savedLong : T.save;
    } else {
      btn.setAttribute('aria-label', `${on ? T.saved : T.save}`);
    }
  });
  if (drawer?.open) renderDrawer();
}

async function toggleWishlist(btn) {
  const handle = btn.dataset.snHandle;
  if (!handle) return;
  btn.classList.remove('sn-pop');
  void btn.offsetWidth;
  btn.classList.add('sn-pop');

  if (Wishlist.has(handle)) {
    Wishlist.remove(handle);
    showToast({ title: T.removedFromWishlist, action: false });
    return;
  }

  const holder = btn.closest('[data-sn-snapshot]');
  const snapshot = readJSONAttr(holder, 'data-sn-snapshot') || { handle, url: `${ROOT}products/${handle}` };
  Wishlist.add(snapshot);
  bump(document.querySelector('[data-sn-wish-open]'));
  showToast({ title: T.addedToWishlist, text: snapshot.title || '', image: snapshot.image || '', action: false });

  if (!snapshot.title || snapshot.price == null) {
    fetchProduct(handle)
      .then((p) => {
        Wishlist.patch(handle, snapshotFromProduct(p));
        syncWishlistUI();
      })
      .catch(() => {});
  }
}

/* ------------------------------------------------------------------ product page button */
function mountProductPageButton() {
  const data = CFG.product;
  if (!data || !data.handle || document.querySelector('.sn-pdp-wish')) return;
  const anchor =
    document.querySelector('main product-form-component .product-form-buttons') ||
    document.querySelector('main product-form-component') ||
    document.querySelector('main h1');
  if (!anchor) return;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'sn-pdp-wish';
  btn.dataset.snWish = '';
  btn.dataset.snHandle = data.handle;
  btn.setAttribute('data-sn-snapshot', JSON.stringify(data));
  btn.innerHTML = `${ICON.heart}<span data-sn-label>${esc(T.save)}</span>`;
  anchor.insertAdjacentElement('afterend', btn);
}

/* ------------------------------------------------------------------ events */
function onClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const wish = target.closest('[data-sn-wish]');
  const add = target.closest('[data-sn-add]');
  const open = target.closest('[data-sn-wish-open]');
  const remove = target.closest('[data-sn-remove]');
  const sizer = target.closest('[data-sn-size-open]');
  if (!wish && !add && !open && !remove && !sizer) return;

  // Card links and view-transition handlers sit underneath: keep them from firing.
  event.preventDefault();
  event.stopPropagation();

  if (sizer) return openSizePicker();
  if (open) return openWishlist();
  if (remove) return Wishlist.remove(remove.dataset.snRemove);
  if (wish) return toggleWishlist(wish);
  if (add && !add.disabled) quickAdd(add.dataset.snHandle, add);
}

function onIntent(event) {
  const target = event.target instanceof Element ? event.target.closest('[data-sn-add][data-sn-handle]') : null;
  if (target) fetchProduct(target.dataset.snHandle).catch(() => {});
}

function init() {
  document.addEventListener('click', onClick, true);
  document.addEventListener('pointerover', onIntent, { passive: true });
  document.addEventListener('touchstart', onIntent, { passive: true });
  document.addEventListener('snatched:wishlist-change', syncWishlistUI);
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) syncWishlistUI();
    if (event.key === SIZE_KEY || event.key === ONLY_FIT_KEY) syncSizeUI();
  });
  window.addEventListener('pageshow', () => {
    syncWishlistUI();
    refreshCartState();
  });

  // Keep the bag buttons in step with the real cart (drawer edits, other tabs, the product form).
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const settle = (res) => {
      const items = res?.detail?.items;
      if (Array.isArray(items) && items.every((i) => i && i.handle)) setCartFromItems(items);
      else refreshCartState(150);
    };
    if (event.promise) event.promise.then(settle).catch(() => refreshCartState(300));
    else refreshCartState(300);
  });

  mountProductPageButton();
  mountProductPageSize();
  syncWishlistUI();
  syncSizeUI();
  refreshCartState();

  // Cards that arrive later (filters, pagination, recommendations) get their heart state right away.
  let queued = false;
  const relevant = (node) =>
    node instanceof Element &&
    !node.closest('.sn-dialog, .sn-toast') &&
    (node.matches('[data-sn-wish], product-form-component') || node.querySelector('[data-sn-wish], product-form-component'));
  new MutationObserver((mutations) => {
    if (queued) return;
    if (!mutations.some((m) => [...m.addedNodes].some(relevant))) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      mountProductPageButton();
      syncWishlistUI();
      syncBagButtons();
      syncFitChips();
    });
  }).observe(document.body, { childList: true, subtree: true });

  // Deep link: /?wishlist (or the old /pages/wishlist page) opens the drawer
  if (new URLSearchParams(location.search).has('wishlist') || /\/pages\/wishlist\/?$/.test(location.pathname)) openWishlist();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

window.Snatched = Object.assign(window.Snatched || {}, { Wishlist, MySize, openWishlist, openSizePicker, quickAdd });
export {};
